import type { GuideLesson } from "../types/guide-content.types.ts"

// "What is EZBuild?" and "How EZBuild works" used to live here as text
// lessons — they're now the visual product story at GuideProductStoryPage
// (RoutesPaths.guide.story), which every new user sees before this list.
export const GUIDE_LESSONS: GuideLesson[] = [
  // Start Here
  {
    id: "start-here-your-first-lead",
    moduleSlug: "start-here",
    order: 1,
    titleKey: "guide.lessons.startHereYourFirstLead.title",
    descriptionKey: "guide.lessons.startHereYourFirstLead.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.startHereYourFirstLead.body.p1"],
    learningPointKeys: ["guide.lessons.startHereYourFirstLead.points.p1"],
    relatedTourId: "leads-pipeline"
  },
  {
    id: "start-here-lead-to-project",
    moduleSlug: "start-here",
    order: 2,
    titleKey: "guide.lessons.startHereLeadToProject.title",
    descriptionKey: "guide.lessons.startHereLeadToProject.description",
    durationMinutes: 4,
    bodyParagraphKeys: [
      "guide.lessons.startHereLeadToProject.body.p1",
      "guide.lessons.startHereLeadToProject.body.p2"
    ],
    learningPointKeys: [
      "guide.lessons.startHereLeadToProject.points.p1",
      "guide.lessons.startHereLeadToProject.points.p2"
    ],
    relatedProductTourStepId: "lead-to-project"
  },
  {
    id: "start-here-understanding-the-project",
    moduleSlug: "start-here",
    order: 3,
    titleKey: "guide.lessons.startHereUnderstandingTheProject.title",
    descriptionKey: "guide.lessons.startHereUnderstandingTheProject.description",
    durationMinutes: 4,
    bodyParagraphKeys: ["guide.lessons.startHereUnderstandingTheProject.body.p1"],
    learningPointKeys: ["guide.lessons.startHereUnderstandingTheProject.points.p1"],
    relatedProductTourStepId: "project-overview"
  },
  {
    id: "start-here-managing-day-to-day",
    moduleSlug: "start-here",
    order: 4,
    titleKey: "guide.lessons.startHereManagingDayToDay.title",
    descriptionKey: "guide.lessons.startHereManagingDayToDay.description",
    durationMinutes: 5,
    bodyParagraphKeys: ["guide.lessons.startHereManagingDayToDay.body.p1"],
    learningPointKeys: ["guide.lessons.startHereManagingDayToDay.points.p1"]
  },

  // Leads — "Understanding Leads" (the Sales learning area)
  {
    id: "leads-what-is-a-lead",
    moduleSlug: "leads",
    order: 1,
    type: "explain",
    titleKey: "guide.lessons.leadsWhatIsALead.title",
    descriptionKey: "guide.lessons.leadsWhatIsALead.description",
    durationMinutes: 2,
    bodyParagraphKeys: ["guide.lessons.leadsWhatIsALead.body.p1"],
    learningPointKeys: ["guide.lessons.leadsWhatIsALead.points.p1"],
    relatedTourId: "leads-pipeline"
  },
  {
    id: "leads-stages",
    moduleSlug: "leads",
    order: 2,
    type: "explain",
    titleKey: "guide.lessons.leadsStages.title",
    descriptionKey: "guide.lessons.leadsStages.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsStages.body.p1"],
    learningPointKeys: ["guide.lessons.leadsStages.points.p1"],
    relatedTourId: "leads-pipeline"
  },
  {
    id: "leads-overview",
    moduleSlug: "leads",
    order: 3,
    type: "walkthrough",
    titleKey: "guide.lessons.leadsOverview.title",
    descriptionKey: "guide.lessons.leadsOverview.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsOverview.body.p1"],
    learningPointKeys: ["guide.lessons.leadsOverview.points.p1"],
    relatedProductTourStepId: "lead-overview"
  },
  {
    id: "leads-scope",
    moduleSlug: "leads",
    order: 4,
    type: "walkthrough",
    titleKey: "guide.lessons.leadsScope.title",
    descriptionKey: "guide.lessons.leadsScope.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsScope.body.p1"],
    learningPointKeys: ["guide.lessons.leadsScope.points.p1"],
    relatedProductTourStepId: "lead-scope"
  },
  {
    id: "leads-estimates",
    moduleSlug: "leads",
    order: 5,
    type: "explain",
    titleKey: "guide.lessons.leadsEstimates.title",
    descriptionKey: "guide.lessons.leadsEstimates.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsEstimates.body.p1"],
    learningPointKeys: ["guide.lessons.leadsEstimates.points.p1"]
  },
  {
    id: "leads-proposals",
    moduleSlug: "leads",
    order: 6,
    type: "walkthrough",
    titleKey: "guide.lessons.leadsProposals.title",
    descriptionKey: "guide.lessons.leadsProposals.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsProposals.body.p1"],
    learningPointKeys: ["guide.lessons.leadsProposals.points.p1"],
    relatedProductTourStepId: "lead-sales-flow"
  },
  {
    id: "leads-contracts",
    moduleSlug: "leads",
    order: 7,
    type: "walkthrough",
    titleKey: "guide.lessons.leadsContracts.title",
    descriptionKey: "guide.lessons.leadsContracts.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsContracts.body.p1"],
    learningPointKeys: ["guide.lessons.leadsContracts.points.p1"],
    relatedProductTourStepId: "lead-sales-flow"
  },
  {
    id: "leads-converting-to-project",
    moduleSlug: "leads",
    order: 8,
    type: "walkthrough",
    titleKey: "guide.lessons.leadsConvertingToProject.title",
    descriptionKey: "guide.lessons.leadsConvertingToProject.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.leadsConvertingToProject.body.p1"],
    learningPointKeys: ["guide.lessons.leadsConvertingToProject.points.p1"],
    relatedProductTourStepId: "lead-to-project"
  },

  // Projects — "Running the Job" (the Projects learning area). Overview is
  // deliberately not covered here — the real tab is unmounted from routing
  // (see docs/kb/decisions/2026-09-14-project-view-index-lands-on-project-
  // management-not-overview.md) and would teach a dead link. Gantt/
  // dependencies are folded into Milestones rather than given their own
  // lesson — they're a view toggle inside that tab, not a separate one.
  {
    id: "projects-understanding-projects",
    moduleSlug: "projects",
    order: 1,
    type: "explain",
    titleKey: "guide.lessons.projectsUnderstandingProjects.title",
    descriptionKey: "guide.lessons.projectsUnderstandingProjects.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.projectsUnderstandingProjects.body.p1"],
    learningPointKeys: ["guide.lessons.projectsUnderstandingProjects.points.p1"],
    relatedProductTourStepId: "project-overview"
  },
  {
    id: "projects-milestones",
    moduleSlug: "projects",
    order: 2,
    type: "walkthrough",
    titleKey: "guide.lessons.projectsMilestones.title",
    descriptionKey: "guide.lessons.projectsMilestones.description",
    durationMinutes: 4,
    bodyParagraphKeys: ["guide.lessons.projectsMilestones.body.p1"],
    learningPointKeys: [
      "guide.lessons.projectsMilestones.points.p1",
      "guide.lessons.projectsMilestones.points.p2"
    ],
    relatedProductTourStepId: "project-milestones",
    requiredPage: "projects.milestones.project_milestone_view"
  },
  {
    id: "projects-tasks",
    moduleSlug: "projects",
    order: 3,
    type: "explain",
    titleKey: "guide.lessons.projectsTasks.title",
    descriptionKey: "guide.lessons.projectsTasks.description",
    durationMinutes: 2,
    bodyParagraphKeys: ["guide.lessons.projectsTasks.body.p1"],
    learningPointKeys: ["guide.lessons.projectsTasks.points.p1"],
    requiredPage: "projects/tasks"
  },
  {
    id: "projects-finance",
    moduleSlug: "projects",
    order: 4,
    type: "walkthrough",
    titleKey: "guide.lessons.projectsFinance.title",
    descriptionKey: "guide.lessons.projectsFinance.description",
    durationMinutes: 4,
    bodyParagraphKeys: ["guide.lessons.projectsFinance.body.p1"],
    learningPointKeys: [
      "guide.lessons.projectsFinance.points.p1",
      "guide.lessons.projectsFinance.points.p2"
    ],
    relatedProductTourStepId: "project-finance",
    requiredPage: "projects.finances.project_finance_view"
  },
  {
    id: "projects-invoices",
    moduleSlug: "projects",
    order: 5,
    type: "walkthrough",
    titleKey: "guide.lessons.projectsInvoices.title",
    descriptionKey: "guide.lessons.projectsInvoices.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.projectsInvoices.body.p1"],
    learningPointKeys: ["guide.lessons.projectsInvoices.points.p1"],
    relatedProductTourStepId: "project-invoices",
    requiredPage: "projects.invoices.project_invoice_view"
  },
  {
    id: "projects-files",
    moduleSlug: "projects",
    order: 6,
    type: "walkthrough",
    titleKey: "guide.lessons.projectsFiles.title",
    descriptionKey: "guide.lessons.projectsFiles.description",
    durationMinutes: 3,
    bodyParagraphKeys: ["guide.lessons.projectsFiles.body.p1"],
    learningPointKeys: ["guide.lessons.projectsFiles.points.p1"],
    relatedProductTourStepId: "project-files",
    requiredPage: "projects.docs.project_doc_view"
  },
  {
    id: "projects-activity",
    moduleSlug: "projects",
    order: 7,
    type: "walkthrough",
    titleKey: "guide.lessons.projectsActivity.title",
    descriptionKey: "guide.lessons.projectsActivity.description",
    durationMinutes: 2,
    bodyParagraphKeys: ["guide.lessons.projectsActivity.body.p1"],
    learningPointKeys: ["guide.lessons.projectsActivity.points.p1"],
    relatedProductTourStepId: "project-activity",
    requiredPage: "projects.features.project_activity_log"
  }
]

export const getLessonsForModule = (moduleSlug: string): GuideLesson[] =>
  GUIDE_LESSONS.filter((lesson) => lesson.moduleSlug === moduleSlug).sort(
    (a, b) => a.order - b.order
  )

export const getGuideLesson = (id: string): GuideLesson | null =>
  GUIDE_LESSONS.find((lesson) => lesson.id === id) ?? null

/** Explicit `relatedLessonIds` when curated; otherwise up to 3 other
 * lessons from the same module — never invented content, just what the
 * module already has. */
export const getRelatedLessons = (lesson: GuideLesson): GuideLesson[] => {
  if (lesson.relatedLessonIds) {
    return lesson.relatedLessonIds
      .map((id) => getGuideLesson(id))
      .filter((related): related is GuideLesson => related !== null)
  }
  return getLessonsForModule(lesson.moduleSlug)
    .filter((sibling) => sibling.id !== lesson.id)
    .slice(0, 3)
}
